// src/components/admin/ResourceManager.jsx

import React, { useState, useEffect } from 'react';
import { Plus, Video, Link as LinkIcon, FileText, Trash2, Globe, Lock, Database, Info, BookOpen, Mic, User, Pencil, X, GripVertical, Layers, ChevronsUp, ChevronsDown, Pin, PinOff } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { sortEpisodes } from '../../utils/podcastOrder';
import { pinnedFirst, pinnedModulesFirst, isManagedTopic } from '../../utils/topicMerge';
import { db } from '../../config/firebase-config';
import { collection, addDoc, deleteDoc, doc, onSnapshot, getDocs, setDoc, getDoc, updateDoc, arrayUnion, arrayRemove, deleteField, writeBatch } from 'firebase/firestore';
import { initialTopicContent } from '../../data/content';
import { customPages } from '../../utils/customNav';

// Global course-topic resources live in a module of the topic doc itself
// (shared /topics or a vertical pack), mirroring the private_topics path.
const GLOBAL_TOPIC_COLLECTIONS = [
    { id: 'topics', label: 'Shared course topics (all organizations)' },
    { id: 'topics_sport', label: 'Sport pack (sport organizations only)' },
    { id: 'topics_corporate', label: 'Corporate pack (corporate organizations only)' }
];

const TIER_LABEL = { base: 'Base', shared: 'Shared', pack: 'Pack', private: 'Private' };

// --- TOPIC VIEW MODEL ----------------------------------------------------------
// Builds what the manager shows for a course topic: the same merge members see
// (content.js base -> shared /topics doc -> the tier being written to), with
// each resource tagged by tier and whether THIS destination may edit it.
//   writeTier 'shared'  -> base + shared are editable (the whole topic is saved
//                          to /topics on first write and becomes "managed")
//   writeTier 'pack'    -> base + shared shown locked; pack resources editable
//   writeTier 'private' -> base + shared shown locked; private resources editable
const mergeTopicTiers = ({ base, sharedDoc, packDoc, privateDoc, writeTier }) => {
    const map = new Map();
    const tag = (m, tier) => ({ ...m, resources: (m.resources || []).map(r => ({ ...r, _tier: tier })) });

    if (isManagedTopic(sharedDoc)) {
        sharedDoc.modules.forEach(m => map.set(m.id, tag(m, 'shared')));
    } else {
        (base || []).forEach(m => map.set(m.id, tag(m, 'base')));
        (sharedDoc?.modules || []).forEach(m => map.set(m.id, tag(m, 'shared'))); // same id -> shared version wins (as in TopicView)
    }

    const union = (modules, tier) => (modules || []).forEach(m => {
        const tagged = tag(m, tier);
        if (map.has(m.id)) {
            const existing = map.get(m.id);
            const merged = [...existing.resources, ...tagged.resources];
            const unique = Array.from(new Map(merged.map(r => [r.id, r])).values());
            map.set(m.id, { ...existing, resources: unique });
        } else {
            map.set(m.id, { ...tagged, _ownedBy: tier });
        }
    });
    if (writeTier === 'pack') union(packDoc?.modules, 'pack');
    if (writeTier === 'private') union(privateDoc?.modules, 'private');

    const ownedTiers = writeTier === 'shared' ? ['base', 'shared'] : [writeTier];
    return Array.from(map.values()).map(m => ({
        ...m,
        // whole module belongs to this destination -> reorderable
        reorderable: writeTier === 'shared' || m._ownedBy === writeTier,
        resources: m.resources.map(r => ({ ...r, editable: ownedTiers.includes(r._tier) }))
    }));
};

const ResourceManager = ({ availableOrgs = [], isRestricted = false, fixedOrgId = null, forcedTargetCollection = null }) => {
    // --- STATE ---
    const [scope, setScope] = useState(isRestricted ? 'private' : 'global');
    // Target Selection
    const [targetType, setTargetType] = useState('library');
    const [targetCollection, setTargetCollection] = useState('brain_health_resources');
    const [targetOrgId, setTargetOrgId] = useState(fixedOrgId || '');
    const [targetTopicId, setTargetTopicId] = useState('');
    // Global + Course Topics: which topic collection receives the resource
    const [globalTopicCollection, setGlobalTopicCollection] = useState('topics');
    // Step badges: under AdminPortal this block follows a "1. pick a library" step;
    // in the super-admin panel it IS step 1.
    const step = (n) => (isRestricted ? n + 1 : n);
    // Data
    const [resourceItems, setResourceItems] = useState([]);   // library modes (flat)
    const [topicModules, setTopicModules] = useState([]);     // topic mode (merged view, grouped)
    const [availableTopics, setAvailableTopics] = useState([]);
    // Topic-mode add options
    const [addModuleId, setAddModuleId] = useState('');       // '' = first module, '__new__' = new module
    const [newModuleTitle, setNewModuleTitle] = useState('');
    const [placeAt, setPlaceAt] = useState('bottom');
    // Form State
    const [newTool, setNewTool] = useState({
        title: '', type: 'Video', content: '', url: '', tags: '',
        // New Fields for Performance 1-2-3
        guest: '', role: '', duration: '',
        ageTiers: ['adult']
    });
    const [isSaving, setIsSaving] = useState(false);
    // EDIT MODE: the resource currently loaded into the form (null = adding).
    const [editingItem, setEditingItem] = useState(null);
    const EMPTY_TOOL = { title: '', type: 'Video', content: '', url: '', tags: '', guest: '', role: '', duration: '', audience: 'both', ageTiers: ['adult'] };

    const effectiveOrgId = isRestricted ? fixedOrgId : targetOrgId;
    const isTopicTarget = targetType === 'topic';
    // Which tier a topic write lands in (see mergeTopicTiers)
    const writeTier = !isTopicTarget ? null : scope === 'private' ? 'private' : (globalTopicCollection === 'topics' ? 'shared' : 'pack');

    const startEdit = (item) => {
        setEditingItem(item);
        setNewTool({
            title: item.title || '',
            type: item.type || 'Video',
            content: item.content || '',
            url: item.url || '',
            tags: Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || ''),
            guest: item.guest || '',
            role: item.role || '',
            duration: item.duration || '',
            audience: Array.isArray(item.verticals) && item.verticals.length > 0 ? item.verticals[0] : 'both',
            ageTiers: Array.isArray(item.ageTiers) && item.ageTiers.length > 0 ? item.ageTiers : ['adult']
        });
    };

    const cancelEdit = () => {
        setEditingItem(null);
        setNewTool(EMPTY_TOOL);
    };

    // --- LIBRARIES CONFIG (Updated) ---
    const LIBRARIES = [
        { id: 'brain_health_resources', label: 'Brain Health Library', tags: 'Physical, Nutrition, Sleep' },
        { id: 'leadership_toolkit', label: 'Leadership Toolkit', tags: 'General, Conflict, Culture' },
        { id: 'kidz_corner_resources', label: 'Kidz Corner', tags: 'Fun, Game, Activity' },
        { id: 'performance_123_episodes', label: 'Performance 1-2-3', tags: 'Mental Toughness, Leadership' },
        { id: 'recharge_sessions', label: 'Reset & Recharge sessions', tags: 'Breathe, Rest, Move, Meditate (one of these places it on the right shelf)' },
        { id: 'finance_resources', label: 'Financial Wellbeing (Scotia)', tags: 'Adults: Budgeting, Saving, Debt, Retirement, Family, Money Stress · Teens: First Paycheque, Saving, Spending, Credit Basics · Kids: Needs vs Wants, Saving Jar, Earning' }
    ];

    // --- EFFECT: HANDLE FORCED COLLECTION (FROM PARENT DROPDOWN) ---
    useEffect(() => {
        if (forcedTargetCollection) {
            setTargetCollection(forcedTargetCollection);
        }
    }, [forcedTargetCollection]);

    // In restricted (org-admin) mode the parent (AdminPortal) already chose "Library",
    // so lock the destination and never expose the course-topic path.
    useEffect(() => {
        if (isRestricted) setTargetType('library');
    }, [isRestricted]);

    // Audience is only meaningful for the global podcast library — clear it
    // whenever the destination changes so a stale choice can't ride along.
    useEffect(() => {
        setNewTool(t => ({ ...t, audience: 'both', ageTiers: ['adult'] }));
        setEditingItem(null); // an edit belongs to the destination it was opened from
        setAddModuleId('');
        setNewModuleTitle('');
    }, [scope, targetType, targetCollection, targetOrgId, targetTopicId, globalTopicCollection]);

    // --- 1. LOAD AVAILABLE TOPICS ---
    useEffect(() => {
        const loadTopics = async () => {
            const snap = await getDocs(collection(db, "topics"));
            const map = new Map();
            Object.entries(initialTopicContent).forEach(([id, t]) => map.set(id, { id, title: t?.title || id }));
            snap.docs.forEach(d => map.set(d.id, { id: d.id, title: d.data().title || map.get(d.id)?.title || d.id }));
            // An org's own sections (utils/customNav.js) are topics too, but only
            // that org has them, so they are offered only when writing to that
            // org's private scope. Without this the client's materials would have
            // no topic to be loaded into.
            if (scope === 'private' && effectiveOrgId) {
                try {
                    const orgSnap = await getDoc(doc(db, "organizations", effectiveOrgId));
                    if (orgSnap.exists()) {
                        customPages(orgSnap.data()).forEach(pg => map.set(pg.id, pg));
                    }
                } catch (e) { console.warn('custom topics unavailable', e); }
            }
            setAvailableTopics(Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title)));
        };
        loadTopics();
    }, [scope, effectiveOrgId]);

    // --- 2a. LISTEN TO LIBRARY RESOURCES (flat) ---
    useEffect(() => {
        setResourceItems([]);
        if (isTopicTarget) return undefined;
        if (isRestricted && !fixedOrgId) return undefined;

        let unsubscribe = () => {};
        if (scope === 'global') {
            unsubscribe = onSnapshot(collection(db, targetCollection), (snapshot) => {
                setResourceItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            });
        } else if (scope === 'private' && effectiveOrgId) {
            const libRef = doc(db, "organizations", effectiveOrgId, "private_libraries", targetCollection);
            unsubscribe = onSnapshot(libRef, (docSnap) => {
                setResourceItems(docSnap.exists() ? (docSnap.data().resources || []) : []);
            });
        }
        return () => unsubscribe();
    }, [scope, targetCollection, effectiveOrgId, isTopicTarget, isRestricted, fixedOrgId]);

    // --- 2b. LISTEN TO A COURSE TOPIC (merged view of every tier that matters) ---
    useEffect(() => {
        setTopicModules([]);
        if (!isTopicTarget || !targetTopicId) return undefined;
        if (writeTier === 'private' && !effectiveOrgId) return undefined;

        let sharedDoc = null, packDoc = null, privateDoc = null;
        const recompute = () => setTopicModules(mergeTopicTiers({
            base: initialTopicContent[targetTopicId]?.modules || [],
            sharedDoc, packDoc, privateDoc, writeTier
        }));
        const unsubs = [];
        unsubs.push(onSnapshot(doc(db, "topics", targetTopicId), (s) => { sharedDoc = s.exists() ? s.data() : null; recompute(); }));
        if (writeTier === 'pack') {
            unsubs.push(onSnapshot(doc(db, globalTopicCollection, targetTopicId), (s) => { packDoc = s.exists() ? s.data() : null; recompute(); }));
        }
        if (writeTier === 'private') {
            unsubs.push(onSnapshot(doc(db, "organizations", effectiveOrgId, "private_topics", targetTopicId), (s) => { privateDoc = s.exists() ? s.data() : null; recompute(); }));
        }
        recompute();
        return () => unsubs.forEach(u => u());
    }, [isTopicTarget, targetTopicId, writeTier, globalTopicCollection, effectiveOrgId]);

    // --- TOPIC WRITES ---
    // The doc this destination writes to
    const topicRef = () => writeTier === 'private'
        ? doc(db, "organizations", effectiveOrgId, "private_topics", targetTopicId)
        : doc(db, writeTier === 'pack' ? globalTopicCollection : "topics", targetTopicId);

    const stripView = (m) => {
        const { reorderable, _ownedBy, ...rest } = m;
        return { ...rest, resources: (m.resources || []).map(({ _tier, editable, _moduleId, ...r }) => r) };
    };

    // Persist the merged view after a change. Shared: the whole topic is saved
    // (and marked managed, so order/deletions are what members see). Pack /
    // private: only this tier's resources are saved; TopicView unions them
    // back into the same modules by id.
    const persistTopic = async (nextModules) => {
        let toWrite;
        if (writeTier === 'shared') {
            toWrite = nextModules.map(stripView);
        } else {
            toWrite = nextModules
                .map(m => ({ ...m, resources: (m.resources || []).filter(r => r._tier === writeTier) }))
                .filter(m => m.resources.length > 0 || m._ownedBy === writeTier)
                .map(stripView);
        }
        await setDoc(topicRef(), {
            modules: toWrite,
            updatedAt: new Date().toISOString(),
            ...(writeTier === 'shared' ? { managed: true } : {})
        }, { merge: true });
    };

    const cloneModules = () => topicModules.map(m => ({ ...m, resources: [...(m.resources || [])] }));

    // --- 3. HANDLERS ---
    const handleAddTool = async (e) => {
        e.preventDefault();
        if (!newTool.title) return;
        setIsSaving(true);

        try {
            const resourceId = `res_${Date.now()}`;
            const tagsArray = typeof newTool.tags === 'string'
                ? newTool.tags.split(',').map(t => t.trim()).filter(Boolean)
                : newTool.tags;

            // Audience -> `verticals` (same field the topic packs use). 'both'
            // stores no field, so untagged items show everywhere. GATED to the
            // global podcast library: `audience` is shared form state, and an
            // ungated spread leaked stale tags into other libraries / private
            // scope (where TopicView's vertical filter would then HIDE the item
            // from the org it was created for). Caught in review.
            const { audience, ageTiers, ...toolFields } = newTool;
            const audienceApplies = scope === 'global' && targetType === 'library' && (targetCollection === 'performance_123_episodes' || targetCollection === 'recharge_sessions');
            // Age tiers only mean something on the finance library (global or an
            // org overlay); everywhere else the field must not ride along.
            const ageApplies = targetType === 'library' && targetCollection === 'finance_resources';
            if (isTopicTarget && !targetTopicId) throw new Error("Select a Topic.");
            if (scope === 'private' && !effectiveOrgId) throw new Error("Organization ID missing.");
            const resourcePayload = {
                ...toolFields,
                id: resourceId,
                tags: tagsArray,
                createdAt: new Date().toISOString(),
                source: scope,
                ...(audienceApplies && (audience === 'sport' || audience === 'corporate') ? { verticals: [audience] } : {}),
                ...(ageApplies ? { ageTiers: (Array.isArray(ageTiers) && ageTiers.length > 0 ? ageTiers : ['adult']) } : {})
            };

            // --- EDIT MODE: update in place instead of adding a new item ---
            if (editingItem) {
                const { id: _newId, createdAt: _c, source: _s, ...updates } = resourcePayload;
                if (scope === 'global' && !isTopicTarget) {
                    // Global libraries: one Firestore doc per resource. Clearing the
                    // audience removes the field rather than leaving a stale tag.
                    const clearVerticals = audienceApplies && !updates.verticals && Array.isArray(editingItem.verticals);
                    await updateDoc(doc(db, targetCollection, editingItem.id), {
                        ...updates,
                        updatedAt: new Date().toISOString(),
                        ...(clearVerticals ? { verticals: deleteField() } : {})
                    });
                } else if (targetType === 'library') {
                    const { _moduleId, _tier, editable, ...original } = editingItem;
                    const replaced = { ...original, ...updates, updatedAt: new Date().toISOString() };
                    const libRef = doc(db, "organizations", effectiveOrgId, "private_libraries", targetCollection);
                    const snap = await getDoc(libRef);
                    const resources = (snap.exists() ? snap.data().resources : []) || [];
                    await setDoc(libRef, {
                        resources: resources.map(r => r.id === editingItem.id ? replaced : r),
                        updatedAt: new Date().toISOString()
                    }, { merge: true });
                } else {
                    // Topic: keyed by module + resource (base ids like 'v1' repeat across modules)
                    if (!editingItem.editable) throw new Error("This resource belongs to another tier and can't be edited from here.");
                    const next = cloneModules().map(m => m.id !== editingItem._moduleId ? m : {
                        ...m,
                        resources: m.resources.map(r => r.id === editingItem.id ? { ...r, ...updates, updatedAt: new Date().toISOString() } : r)
                    });
                    await persistTopic(next);
                }
                setEditingItem(null);
                setNewTool(EMPTY_TOOL);
                alert("Resource Updated!");
                return;
            }

            if (scope === 'global' && !isTopicTarget) {
                await addDoc(collection(db, targetCollection), resourcePayload);
            } else if (targetType === 'library') {
                const libRef = doc(db, "organizations", effectiveOrgId, "private_libraries", targetCollection);
                await setDoc(libRef, {
                    resources: arrayUnion(resourcePayload),
                    updatedAt: new Date().toISOString()
                }, { merge: true });
            } else {
                // Topic: add into the chosen module (top or bottom) or a new module.
                // Additive tiers (pack/private) are unioned AFTER base resources by
                // the app, so "top" also pins the resource; shared writes control
                // the order directly.
                const next = cloneModules();
                const res = {
                    ...resourcePayload,
                    _tier: writeTier,
                    ...(placeAt === 'top' && writeTier !== 'shared' ? { pinned: true } : {})
                };
                const chosen = addModuleId || next[0]?.id || '__new__';
                if (chosen === '__new__') {
                    const title = newModuleTitle.trim();
                    if (!title) throw new Error("Give the new module a title.");
                    next.push({ id: `mod_${Date.now()}`, title, resources: [res], _ownedBy: writeTier, reorderable: true });
                } else {
                    const mod = next.find(m => m.id === chosen);
                    if (!mod) throw new Error("Pick a module to add to.");
                    if (placeAt === 'top') mod.resources.unshift(res); else mod.resources.push(res);
                }
                await persistTopic(next);
                setNewModuleTitle('');
                if (chosen === '__new__') setAddModuleId('');
            }

            setNewTool({ title: '', type: 'Video', content: '', url: '', tags: '', guest: '', role: '', duration: '', audience: 'both', ageTiers: ['adult'] });
            alert("Resource Added Successfully!");

        } catch (error) {
            alert("Error: " + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteTool = async (item) => {
        if (!window.confirm("Delete this resource?")) return;

        try {
            if (scope === 'global' && !isTopicTarget) {
                await deleteDoc(doc(db, targetCollection, item.id));
            } else if (targetType === 'library') {
                const libRef = doc(db, "organizations", effectiveOrgId, "private_libraries", targetCollection);
                await updateDoc(libRef, { resources: arrayRemove(item) });
            } else {
                if (!item.editable) throw new Error("This resource belongs to another tier and can't be deleted from here.");
                const next = cloneModules().map(m => m.id !== item._moduleId ? m : {
                    ...m,
                    resources: m.resources.filter(r => r.id !== item.id)
                });
                await persistTopic(next);
                if (editingItem?.id === item.id && editingItem?._moduleId === item._moduleId) cancelEdit();
            }
        } catch (e) { alert(e.message); }
    };

    const activeLib = LIBRARIES.find(l => l.id === targetCollection);
    const isPodcastMode = targetType === 'library' && targetCollection === 'performance_123_episodes';
    const isRechargeMode = targetType === 'library' && targetCollection === 'recharge_sessions';
    const isFinanceMode = targetType === 'library' && targetCollection === 'finance_resources';

    // --- DRAG-AND-DROP ORDER (global podcast library only) ---
    // The player sorts by a numeric `order` (manual first, then newest), so the
    // CMS shows the same sequence and lets you drag it. Persisted in one batch.
    const canReorder = isPodcastMode && scope === 'global';
    const sortedItems = canReorder ? sortEpisodes(resourceItems) : resourceItems;

    const handleReorder = async (result) => {
        if (!result.destination || result.destination.index === result.source.index) return;
        const next = Array.from(sortedItems);
        const [moved] = next.splice(result.source.index, 1);
        next.splice(result.destination.index, 0, moved);
        const withOrder = next.map((it, i) => ({ ...it, order: i }));
        setResourceItems(withOrder); // optimistic; the snapshot listener confirms
        try {
            const batch = writeBatch(db);
            withOrder.forEach((it) => batch.update(doc(db, targetCollection, it.id), { order: it.order }));
            await batch.commit();
        } catch (e) {
            alert("Reorder failed: " + e.message);
        }
    };

    // --- DRAG-AND-DROP ORDER (course topics: within one module) ---
    // Module order: drag (Shared mode — the saved order is what members see)
    const displayModules = pinnedModulesFirst(topicModules);
    const handleModuleReorder = async (result) => {
        const { source, destination } = result;
        if (!destination || destination.index === source.index) return;
        if (writeTier !== 'shared') return;
        const next = Array.from(displayModules);
        const [moved] = next.splice(source.index, 1);
        next.splice(destination.index, 0, moved);
        setTopicModules(next); // optimistic
        try { await persistTopic(next); } catch (e) { alert("Reorder failed: " + e.message); }
    };

    // Pin to top (pack / private — owned modules float above the base content)
    const togglePinModule = async (mod) => {
        const next = cloneModules().map(m => m.id === mod.id ? { ...m, pinned: !m.pinned } : m);
        try { await persistTopic(next); } catch (e) { alert("Pin failed: " + e.message); }
    };

    const handleTopicReorder = async (result) => {
        if (result.type === 'MODULE') return handleModuleReorder(result);
        const { source, destination } = result;
        if (!destination || destination.droppableId !== source.droppableId || destination.index === source.index) return;
        const mod = topicModules.find(m => m.id === source.droppableId);
        if (!mod || !mod.reorderable) return;
        const shown = pinnedFirst(mod.resources);
        const reordered = Array.from(shown);
        const [moved] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, moved);
        const next = cloneModules().map(m => m.id !== mod.id ? m : { ...m, resources: reordered });
        setTopicModules(next); // optimistic; the listener confirms
        try {
            await persistTopic(next);
        } catch (e) {
            alert("Reorder failed: " + e.message);
        }
    };

    // Delete a whole module you own (Shared: any module — the topic is managed
    // after the first save, so the deletion sticks; pack/private: modules you created)
    const handleDeleteModule = async (mod) => {
        const count = (mod.resources || []).length;
        if (!window.confirm(`Delete the module "${mod.title}"${count ? ` and its ${count} resource(s)` : ''}? This cannot be undone.`)) return;
        try {
            const next = cloneModules().filter(m => m.id !== mod.id);
            await persistTopic(next);
            if (editingItem?._moduleId === mod.id) cancelEdit();
            if (addModuleId === mod.id) setAddModuleId('');
        } catch (e) { alert("Delete failed: " + e.message); }
    };

    const tierBadge = (tier) => {
        const label = tier === 'pack'
            ? (globalTopicCollection === 'topics_sport' ? 'Sport pack' : 'Corporate pack')
            : TIER_LABEL[tier] || tier;
        const cls = tier === 'private'
            ? 'bg-red-50 text-red-600 border-red-100'
            : tier === 'pack'
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : tier === 'shared'
                    ? 'bg-blue-50 text-blue-600 border-blue-100'
                    : 'bg-slate-100 text-slate-500 border-slate-200';
        return <span className={`text-[10px] px-2 py-1 rounded font-bold border ${cls}`}>{label}</span>;
    };

    return (
        <div className="space-y-6">

            {/* SECTION 1: WHERE IS THIS GOING? — scope + destination up front, so a
                global upload never has to detour through Private to pick a destination */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 border-b pb-2 flex items-center gap-2">
                    <span className="bg-slate-100 text-slate-600 w-6 h-6 rounded-full flex items-center justify-center text-xs">{step(1)}</span>
                    {isRestricted ? 'Configuration' : 'Where is this going?'}
                </h2>

                <div className="space-y-6">
                    {isRestricted ? (
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Content Scope</label>
                            <div className="p-3 bg-blue-50 text-blue-700 rounded-xl flex items-center gap-3 border border-blue-100">
                                <Lock size={18} />
                                <div>
                                    <p className="text-sm font-bold">Private Organization</p>
                                    <p className="text-[10px] opacity-80">Content is only visible to your team.</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Content Scope</label>
                                <div className="flex flex-col sm:flex-row bg-slate-100 p-1 rounded-xl gap-1">
                                    <button onClick={() => setScope('global')} className={`flex-1 py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-2 ${scope === 'global' ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
                                        <Globe size={14}/> Global
                                    </button>
                                    <button onClick={() => setScope('private')} className={`flex-1 py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-2 ${scope === 'private' ? 'bg-white shadow text-red-600' : 'text-slate-500'}`}>
                                        <Lock size={14}/> Private
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Resource Destination</label>
                                <div className="flex flex-col sm:flex-row bg-slate-100 p-1 rounded-xl gap-1">
                                    <button onClick={() => setTargetType('library')} className={`flex-1 py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-2 ${targetType === 'library' ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
                                        <BookOpen size={14} /> Libraries
                                    </button>
                                    <button onClick={() => setTargetType('topic')} className={`flex-1 py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-2 ${targetType === 'topic' ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
                                        <FileText size={14} /> Course Topics
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {scope === 'private' && !isRestricted && (
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Organization</label>
                            <select className="w-full p-2.5 border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-brand-primary" value={targetOrgId} onChange={e => setTargetOrgId(e.target.value)}>
                                <option value="">-- Select Org --</option>
                                {availableOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                        </div>
                    )}

                    {scope === 'global' && targetType === 'topic' && !isRestricted && (
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Publish to</label>
                            <select
                                className={`w-full p-2.5 border rounded-lg text-sm font-bold outline-none focus:ring-2 focus:ring-brand-primary ${globalTopicCollection === 'topics' ? 'bg-slate-50' : 'bg-amber-50 border-amber-300 text-amber-900'}`}
                                value={globalTopicCollection}
                                onChange={e => setGlobalTopicCollection(e.target.value)}
                            >
                                {GLOBAL_TOPIC_COLLECTIONS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                            </select>
                            <p className="text-[11px] text-slate-500 mt-1 font-medium">
                                {globalTopicCollection === 'topics'
                                    ? 'Shared: you are editing the topic every organization sees. The first save takes the topic over from the built-in content, so order and deletions stick.'
                                    : 'Pack: built-in and shared content is shown locked for context; what you add here only ever appears for organizations in this vertical. Use the pin on a module you created to show it ABOVE the built-in modules.'}
                            </p>
                        </div>
                    )}

                    <div>
                        {!isRestricted && (<>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2">
                            {targetType === 'library' ? "Target Library Collection" : "Target Course Topic"}
                        </label>

                        {targetType === 'library' ? (
                            <select
                                className="w-full p-2.5 border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-brand-primary bg-slate-50"
                                value={targetCollection}
                                onChange={e => setTargetCollection(e.target.value)}
                                disabled={!!forcedTargetCollection}
                            >
                                {LIBRARIES.map(lib => <option key={lib.id} value={lib.id}>{lib.label}</option>)}
                            </select>
                        ) : (
                            <select
                                className="w-full p-2.5 border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-brand-primary bg-slate-50"
                                value={targetTopicId}
                                onChange={e => setTargetTopicId(e.target.value)}
                            >
                                <option value="">-- Select Topic --</option>
                                {availableTopics.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                            </select>
                        )}
                        </>)}

                        {targetType === 'library' && activeLib && (
                            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 mt-3">
                                <p className="text-[10px] font-bold text-blue-800 mb-1 flex items-center gap-1"><Info size={12}/> Recommended Tags:</p>
                                <p className="text-[10px] text-blue-600 leading-relaxed font-mono">
                                    {activeLib.tags}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* SECTION 2: ADD FORM */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 border-b pb-2 flex items-center gap-2">
                    <span className="bg-slate-100 text-slate-600 w-6 h-6 rounded-full flex items-center justify-center text-xs">{step(2)}</span>
                    Create Resource
                </h2>
                <form onSubmit={handleAddTool} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input className="w-full p-3 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-brand-primary" placeholder="Title / Episode Name" value={newTool.title} onChange={e=>setNewTool({...newTool,title:e.target.value})}/>
                        <input className="w-full p-3 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-brand-primary" placeholder="Tags (e.g. Sleep, Physical)" value={newTool.tags} onChange={e=>setNewTool({...newTool,tags:e.target.value})}/>
                    </div>

                    {/* --- TOPIC PLACEMENT: which module, top or bottom --- */}
                    {isTopicTarget && !editingItem && (
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 animate-in slide-in-from-top-2">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Add to module</label>
                                <select
                                    className="w-full p-3 border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-brand-primary bg-slate-50"
                                    value={addModuleId || topicModules[0]?.id || '__new__'}
                                    onChange={e => setAddModuleId(e.target.value)}
                                    disabled={!targetTopicId}
                                >
                                    {topicModules.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                                    <option value="__new__">+ New module…</option>
                                </select>
                                {(addModuleId === '__new__' || topicModules.length === 0) && (
                                    <input
                                        className="w-full mt-2 p-3 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-brand-primary"
                                        placeholder="New module title (e.g. Start Here)"
                                        value={newModuleTitle}
                                        onChange={e => setNewModuleTitle(e.target.value)}
                                    />
                                )}
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Place at</label>
                                <div className="flex bg-slate-100 p-1 rounded-lg gap-1">
                                    <button type="button" onClick={() => setPlaceAt('top')} className={`px-4 py-2.5 text-xs font-bold rounded-md flex items-center gap-1.5 transition-all ${placeAt === 'top' ? 'bg-white shadow text-slate-800' : 'text-slate-400'}`}>
                                        <ChevronsUp size={14} /> Top
                                    </button>
                                    <button type="button" onClick={() => setPlaceAt('bottom')} className={`px-4 py-2.5 text-xs font-bold rounded-md flex items-center gap-1.5 transition-all ${placeAt === 'bottom' ? 'bg-white shadow text-slate-800' : 'text-slate-400'}`}>
                                        <ChevronsDown size={14} /> Bottom
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- DYNAMIC FIELDS FOR PODCASTS --- */}
                    {isPodcastMode && (
                        <div className="grid grid-cols-3 gap-4 animate-in slide-in-from-top-2">
                            <div className="relative">
                                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                                <input className="w-full p-3 pl-9 border rounded-lg text-sm" placeholder="Guest Name" value={newTool.guest} onChange={e=>setNewTool({...newTool,guest:e.target.value})}/>
                            </div>
                            <input className="w-full p-3 border rounded-lg text-sm" placeholder="Guest Role (e.g. CEO)" value={newTool.role} onChange={e=>setNewTool({...newTool,role:e.target.value})}/>
                            <input className="w-full p-3 border rounded-lg text-sm" placeholder="Duration (e.g. 24:15)" value={newTool.duration} onChange={e=>setNewTool({...newTool,duration:e.target.value})}/>
                        </div>
                    )}

                    {/* --- AUDIENCE (vertical) — podcasts are a global library, so this
                        is what keeps sport episodes off the corporate side. Global scope
                        only: a single org's private library has no "other vertical". --- */}
                    {isRechargeMode && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2">
                            <div className="relative">
                                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                                <input className="w-full p-3 pl-9 border rounded-lg text-sm" placeholder="Creator / channel (shown on the card)" value={newTool.guest} onChange={e=>setNewTool({...newTool,guest:e.target.value})}/>
                            </div>
                            <input className="w-full p-3 border rounded-lg text-sm" placeholder="Short description" value={newTool.content} onChange={e=>setNewTool({...newTool,content:e.target.value})}/>
                        </div>
                    )}

                    {(isPodcastMode || isRechargeMode) && scope === 'global' && (
                        <div className="animate-in slide-in-from-top-2">
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Audience</label>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { id: 'both', label: 'Both — every organization' },
                                    { id: 'sport', label: 'Sport orgs only' },
                                    { id: 'corporate', label: 'Corporate orgs only' }
                                ].map(a => (
                                    <button key={a.id} type="button" onClick={() => setNewTool({ ...newTool, audience: a.id })}
                                        className={`px-4 py-2 text-xs font-bold border rounded-lg transition-all ${(newTool.audience || 'both') === a.id ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-500 hover:border-slate-300'}`}>
                                        {a.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {isFinanceMode && (
                        <div className="animate-in slide-in-from-top-2">
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Age tiers (who sees it)</label>
                            <div className="flex flex-wrap gap-2">
                                {[{ id: 'kid', label: 'Kids (under 13)' }, { id: 'teen', label: 'Teens (13–17)' }, { id: 'adult', label: 'Adults' }].map(a => {
                                    const on = (newTool.ageTiers || ['adult']).includes(a.id);
                                    return (
                                        <button key={a.id} type="button"
                                            onClick={() => setNewTool(t => {
                                                const cur = t.ageTiers || ['adult'];
                                                const next = cur.includes(a.id) ? cur.filter(x => x !== a.id) : [...cur, a.id];
                                                return { ...t, ageTiers: next.length ? next : ['adult'] };
                                            })}
                                            className={`px-4 py-2 text-xs font-bold border rounded-lg transition-all ${on ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-500 hover:border-slate-300'}`}>
                                            {a.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1">Pick every tier the item suits. Untagged items count as adult-only, so a child never sees adult content by accident. The Tags field decides which section it lands in — see the library hint above.</p>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                        {['Video', 'Audio', 'Link', 'PDF', 'Template'].map(t => (
                            <button key={t} type="button" onClick={()=>setNewTool({...newTool,type:t})} className={`px-4 py-2 text-xs font-bold border rounded-lg transition-all ${newTool.type === t ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 hover:border-slate-300'}`}>
                                {t}
                            </button>
                        ))}
                    </div>

                    {newTool.type === 'Template' ?
                        <textarea className="w-full p-3 border rounded-lg text-sm h-32 font-mono" placeholder="Content / Description..." value={newTool.content} onChange={e=>setNewTool({...newTool,content:e.target.value})}/> :
                        <div className="flex flex-col gap-2">
                            <input className="w-full p-3 border rounded-lg text-sm" placeholder={newTool.type === 'Video' ? "YouTube URL..." : "Resource URL..."} value={newTool.url} onChange={e=>setNewTool({...newTool,url:e.target.value})}/>
                            {isPodcastMode && <input className="w-full p-3 border rounded-lg text-sm" placeholder="Episode Description..." value={newTool.content} onChange={e=>setNewTool({...newTool,content:e.target.value})}/>}
                        </div>
                    }

                    {editingItem && (
                        <div className="flex items-center justify-between gap-3 text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <span className="flex items-center gap-2 min-w-0"><Pencil size={14} className="shrink-0"/> <span className="truncate">Editing: {editingItem.title}</span></span>
                            <button type="button" onClick={cancelEdit} className="flex items-center gap-1 text-slate-500 hover:text-slate-800 shrink-0"><X size={14}/> Cancel</button>
                        </div>
                    )}
                    <button
                        disabled={isSaving || (scope === 'private' && !isRestricted && !targetOrgId) || (isTopicTarget && !targetTopicId)}
                        className={`w-full py-3 text-white rounded-xl font-bold hover:brightness-110 transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${editingItem ? 'bg-amber-500' : 'bg-brand-primary'}`}
                    >
                        {editingItem ? <Pencil size={18}/> : <Plus size={18}/>} {isSaving ? 'Saving...' : (editingItem ? 'Save Changes' : 'Save Resource')}
                    </button>
                </form>
            </div>

            {/* SECTION 3: LIST */}
            <div className="space-y-4">
                <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2 pl-1 flex items-center gap-2">
                    <span className="bg-slate-100 text-slate-600 w-6 h-6 rounded-full flex items-center justify-center text-xs">{step(3)}</span>
                    Current Resources
                </h2>

                {((isTopicTarget && topicModules.length === 0) || (!isTopicTarget && resourceItems.length === 0)) && (
                    <div className="p-12 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                        <Database size={32} className="mx-auto mb-2 opacity-50"/>
                        <p>{isTopicTarget
                            ? (!targetTopicId
                                ? (scope === 'private' ? 'Select an Org and Topic to view resources.' : 'Select a Topic to view resources.')
                                : 'This topic has no content yet — add the first resource above.')
                            : 'No resources found in this collection.'}</p>
                    </div>
                )}

                {/* One item renderer, used by both the plain grid and the draggable lists */}
                {(() => {
                    const renderItem = (item, idx, handleProps = null) => {
                        const locked = isTopicTarget && !item.editable;
                        return (
                        <div key={`${item._moduleId || ''}:${item.id || idx}`} className={`bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex justify-between items-start animate-in fade-in transition-colors group ${locked ? 'opacity-80' : 'hover:border-brand-primary'}`}>
                            <div className="flex gap-4 min-w-0">
                                {handleProps && (
                                    <div {...handleProps} title="Drag to reorder" className="self-center p-1 -ml-2 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing shrink-0">
                                        <GripVertical size={18}/>
                                    </div>
                                )}
                                <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100 shrink-0 group-hover:bg-blue-50 group-hover:text-brand-primary transition-colors">
                                    {item.type === 'Video' ? <Video size={20}/> : item.type === 'Audio' ? <Mic size={20}/> : item.type === 'Link' ? <LinkIcon size={20}/> : <FileText size={20}/>}
                                </div>
                                <div className="min-w-0">
                                    <h4 className="font-bold text-slate-900 truncate">{item.title}</h4>
                                    {item.guest && <p className="text-xs text-slate-500">ft. {item.guest}</p>}
                                    {Array.isArray(item.verticals) && item.verticals.length > 0 && (
                                        <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                                            {item.verticals.includes('sport') ? 'Sport only' : 'Corporate only'}
                                        </span>
                                    )}
                                    {Array.isArray(item.ageTiers) && item.ageTiers.length > 0 && (
                                        <span className="inline-block mt-1 ml-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                                            {item.ageTiers.join(' + ')}
                                        </span>
                                    )}
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        <span className="text-[10px] bg-slate-100 px-2 py-1 rounded font-bold uppercase text-slate-500">{item.type}</span>
                                        {item.tags && Array.isArray(item.tags) && item.tags.map(t => (
                                            <span key={t} className="text-[10px] bg-blue-50 text-blue-600 px-2 py-1 rounded font-bold border border-blue-100">{t}</span>
                                        ))}
                                        {isTopicTarget ? tierBadge(item._tier) : (scope === 'private' && <span className="text-[10px] bg-red-50 text-red-600 px-2 py-1 rounded font-bold border border-red-100">Private</span>)}
                                        {item.pinned && <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-1 rounded font-bold border border-emerald-100 flex items-center gap-1"><Pin size={10}/> Top</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center shrink-0">
                                {locked ? (
                                    <span title="Belongs to another tier — edit it from that destination" className="p-2 text-slate-300"><Lock size={16}/></span>
                                ) : (<>
                                    <button type="button" onClick={() => startEdit(item)} title="Edit" className={`p-2 rounded-lg transition-colors ${editingItem?.id === item.id && (editingItem?._moduleId || null) === (item._moduleId || null) ? 'text-amber-600 bg-amber-50' : 'text-slate-300 hover:text-brand-primary hover:bg-blue-50'}`}>
                                        <Pencil size={18}/>
                                    </button>
                                    <button type="button" onClick={() => handleDeleteTool(item)} title="Delete" className="p-2 text-slate-300 hover:text-red-500 rounded-lg transition-colors hover:bg-red-50">
                                        <Trash2 size={18}/>
                                    </button>
                                </>)}
                            </div>
                        </div>
                        );
                    };

                    // COURSE TOPIC: grouped by module, drag to reorder within a module
                    if (isTopicTarget) {
                        if (topicModules.length === 0) return null;
                        return (
                            <DragDropContext onDragEnd={handleTopicReorder}>
                                <Droppable droppableId="modules" type="MODULE" isDropDisabled={writeTier !== 'shared'}>
                                {(mdp) => (
                                <div ref={mdp.innerRef} {...mdp.droppableProps} className="space-y-5">
                                    {displayModules.map((m, mIdx) => {
                                        const shown = pinnedFirst(m.resources || []).map(r => ({ ...r, _moduleId: m.id }));
                                        const canDragModule = writeTier === 'shared';
                                        const canPin = writeTier !== 'shared' && m.reorderable;
                                        return (
                                            <Draggable key={m.id} draggableId={`mod:${m.id}`} index={mIdx} isDragDisabled={!canDragModule}>
                                            {(mdg, msnap) => (
                                            <div ref={mdg.innerRef} {...mdg.draggableProps} className={`bg-slate-50 border rounded-2xl p-3 ${msnap.isDragging ? 'border-brand-primary shadow-lg' : 'border-slate-200'} ${m.pinned ? 'ring-1 ring-amber-300' : ''}`}>
                                                <div className="flex items-center justify-between gap-3 px-2 py-1 mb-2">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        {canDragModule && (
                                                            <div {...mdg.dragHandleProps} title="Drag to reorder modules" className="p-1 -ml-1 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing shrink-0"><GripVertical size={16}/></div>
                                                        )}
                                                        {m.reorderable ? <Layers size={16} className="text-brand-primary shrink-0"/> : <Lock size={16} className="text-slate-400 shrink-0"/>}
                                                        <h3 className="font-bold text-slate-800 text-sm truncate">{m.title}</h3>
                                                        <span className="text-[10px] font-bold text-slate-400 shrink-0">{shown.length}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        {m.pinned && <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 flex items-center gap-1"><Pin size={11}/> Top</span>}
                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                            {m.reorderable ? 'Drag to reorder' : 'Locked here'}
                                                        </span>
                                                        {canPin && (
                                                            <button type="button" onClick={() => togglePinModule(m)} title={m.pinned ? 'Unpin — back below the built-in modules' : 'Pin to top — show above the built-in modules'} className={`p-1.5 rounded-lg transition-colors ${m.pinned ? 'text-amber-600 bg-amber-50 hover:bg-amber-100' : 'text-slate-300 hover:text-amber-600 hover:bg-amber-50'}`}>
                                                                {m.pinned ? <PinOff size={15}/> : <Pin size={15}/>}
                                                            </button>
                                                        )}
                                                        {m.reorderable && (
                                                            <button type="button" onClick={() => handleDeleteModule(m)} title="Delete this module" className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                                                <Trash2 size={15}/>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <Droppable droppableId={m.id} type="RESOURCE" isDropDisabled={!m.reorderable}>
                                                    {(dp) => (
                                                        <div ref={dp.innerRef} {...dp.droppableProps} className="space-y-2">
                                                            {shown.map((item, idx) => (
                                                                <Draggable key={`${m.id}:${item.id}`} draggableId={`${m.id}:${item.id}`} index={idx} isDragDisabled={!m.reorderable}>
                                                                    {(dg, snap) => (
                                                                        <div ref={dg.innerRef} {...dg.draggableProps} className={snap.isDragging ? "opacity-90 rotate-[0.5deg]" : ""}>
                                                                            {renderItem(item, idx, m.reorderable ? dg.dragHandleProps : null)}
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            ))}
                                                            {dp.placeholder}
                                                            {shown.length === 0 && <p className="text-xs text-slate-400 px-2 py-3">No resources in this module yet.</p>}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </div>
                                            )}
                                            </Draggable>
                                        );
                                    })}
                                    {mdp.placeholder}
                                </div>
                                )}
                                </Droppable>
                            </DragDropContext>
                        );
                    }

                    return canReorder ? (
                        <DragDropContext onDragEnd={handleReorder}>
                            <Droppable droppableId="podcast-order">
                                {(dp) => (
                                    <div ref={dp.innerRef} {...dp.droppableProps} className="space-y-3">
                                        {sortedItems.map((item, idx) => (
                                            <Draggable key={item.id} draggableId={String(item.id)} index={idx}>
                                                {(dg, snap) => (
                                                    <div ref={dg.innerRef} {...dg.draggableProps} className={snap.isDragging ? "opacity-90 rotate-[0.5deg]" : ""}>
                                                        {renderItem(item, idx, dg.dragHandleProps)}
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {dp.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {sortedItems.map((item, idx) => renderItem(item, idx))}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

export default ResourceManager;
